import { CellContext } from '@tanstack/react-table';
import { Helpers } from 'application/Shared/Helpers';
import React from 'react';

import {
    DateFormatCell,
    IconCellType,
    IconDateOver30daysCell,
    StringCell
} from '../../Cells';
import { Header } from '../Header';
import { CellType, ColumnBuilder, ColumnDef, CustomColumnMeta } from '../types';

export const buildColumn = <
    TData,
    TValue extends string | number | Date | null
>(
    column: ColumnBuilder<TData>
): ColumnDef<TData, TValue> => {
    const {
        cellType,
        enableColumnFilter,
        filterFn,
        headerIcon,
        id,
        meta: metaProps,
        name,
        property,
        props: extraProps = {},
        size,
        sortingFn,
        testId
    } = column;
    const accessorKey = property;

    let cell: (props: CellContext<TData, TValue>) => React.ReactElement;
    cell = StringCell;

    if (cellType === CellType.DateFormat) {
        cell = (props: CellContext<TData, TValue>) => (
            <DateFormatCell {...props} {...extraProps} />
        );
    }

    if (cellType === CellType.Icon) {
        cell = (props: CellContext<TData, TValue>) => (
            <IconDateOver30daysCell
                {...props}
                iconCellProps={extraProps.iconCellProps as IconCellType}
            />
        );
    }

    const icon = headerIcon && {
        size: headerIcon?.size,
        src: headerIcon?.src,
        tooltipText: headerIcon?.tooltipText,
        variant: headerIcon?.variant
    };
    const meta: CustomColumnMeta<TData> = {
        icon,
        ...metaProps
    };
    return {
        accessorKey,
        cell,
        enableColumnFilter,
        filterFn,
        header: Header,
        id,
        meta,
        name,
        size,
        sortingFn,
        testId
    };
};

export const buildColumns = <
    TData,
    TValue extends string | number | Date | null
>(
    dynamicColumns: ColumnBuilder<TData>[],
    existingColumns: ColumnDef<TData, TValue>[],
    data: TData[]
): ColumnDef<TData, TValue>[] => {
    const newColumns = dynamicColumns.reduce<
        Record<string, ColumnBuilder<TData>>
    >(
        (acc, column) => ({
            ...acc,
            [column.id]: { ...column }
        }),
        {}
    );
    const columns = [...existingColumns];
    Object.keys(newColumns).forEach((key) => {
        const dataKey = newColumns[key].property;
        const found =
            data?.length === 0 ||
            data?.find((d) => Helpers.getNestedValue(d, dataKey));
        const columnBuilder = newColumns[key];
        if (found) {
            delete columnBuilder.headerIcon;
        }
        const column = buildColumn<TData, TValue>(columnBuilder);
        const { positionFromEnd } = columnBuilder;
        const positionIndex = positionFromEnd
            ? columns.length - positionFromEnd
            : columns.length;
        columns.splice(positionIndex, 0, column);
    });
    return columns;
};
